import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import Breadcrumb from '../components/Breadcrumb';
import BlogCard from '../components/BlogCard';
import ProductLineup from '../components/news/ProductLineup';
import PerformanceFeatures from '../components/news/PerformanceFeatures';
import ProductImages from '../components/news/ProductImages';
import { validateArticle, transformArticle } from '../utils/contentfulHelpers';
import { client } from '../utils/contentful';
import Button from '../components/Button';

// Helper function to convert title to URL-friendly slug
const toUrlFriendly = (str) => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

const InTheNews = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [newsList, setNewsList] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const itemsPerPage = 8;

  // Get current section from URL
  const currentSection = location.pathname.startsWith('/industrial') ? 'industrial' : 'household';

  useEffect(() => {
    const fetchNews = async () => {
      try {
        console.log('Fetching news articles from Contentful...');
        const response = await client.getEntries({
          content_type: 'newsArticle-2',
          order: '-fields.date'
        });
        
        console.log('Raw Contentful response:', response);
        
        const validatedNews = response.items.map(item => {
          console.log('Processing item:', item);
          const article = {
            id: item.sys.id,
            title: item.fields.title,
            source: item.fields.source,
            date: item.fields.date,
            image: item.fields.image,
            excerpt: item.fields.excerpt,
            content: item.fields.content,
            additionalContent: item.fields.additionalContent,
            postContactContent: item.fields.postContactContent,
            productImages: item.fields.productImages,
            performanceFeatures: item.fields.performanceFeatures,
            productLineup: item.fields.productLineup,
            contact: item.fields.contact,
            tags: item.fields.tags
          };

          const { isValid, errors } = validateArticle(article);
          if (!isValid) {
            console.error('Article validation failed:', errors);
            return null;
          }

          const transformedArticle = transformArticle(article);
          console.log('Transformed article:', transformedArticle);
          return transformedArticle;
        }).filter(Boolean);

        console.log('Final validated news list:', validatedNews);
        setNewsList(validatedNews);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNews();
  }, []);

  const handleArticleClick = (article) => {
    console.log('Handling article click:', article);
    const urlFriendlyTitle = toUrlFriendly(article.title);
    console.log('URL friendly title:', urlFriendlyTitle);
    navigate(`/${currentSection}/news/${urlFriendlyTitle}`);
  };

  const filteredNews = newsList.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredNews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentNews = filteredNews.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  const handleClose = () => {
    setIsFocused(false);
    setSearchTerm('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading news articles...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-xl mb-4">Error loading news articles</div>
        <Button
          onClick={() => window.location.reload()}
          variant="primary"
          size="medium"
        >
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>News | RuggedRed</title>
        <meta name="description" content="Latest news and updates from RuggedRed, your trusted source for professional-grade cleaning products." />
      </Helmet>

      {/* Header Section */}
      <div className="bg-gradient-to-b from-[#D3242A] to-[#B31E23] text-white py-16 relative">
        <div className="container mx-auto px-4">
          <h1 
            className="text-4xl md:text-5xl font-bold text-center mb-4"
            style={{ fontFamily: "Geogrotesque, sans-serif" }}
          >
            IN THE NEWS
          </h1>
          <p className="text-xl text-center max-w-2xl mx-auto">
            Stay updated with the latest news and media coverage about RuggedRed
          </p>
        </div>
        {/* Wave SVG */}
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 10 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          className="absolute bottom-0 left-0 right-0 z-0"
          style={{ overflow: 'visible' }}
        >
          <div className="relative w-full" style={{ height: '122px', overflow: 'visible' }}>
            <svg
              viewBox="0 0 1440 120"
              fill="white"
              xmlns="http://www.w3.org/2000/svg"
              className="absolute bottom-0 left-0 w-full h-full"
              preserveAspectRatio="none"
              style={{ transform: 'scale(1.5) translateY(3px)' }}
            >
              <path
                d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z"
              />
            </svg>
          </div>
        </motion.div>
      </div>

      <div className="container mx-auto px-4 py-8 bg-white relative z-10">
        <div className="mt-8">
          <Breadcrumb />
        </div>
        
        {/* Search Section */}
        <div className="relative w-full max-w-2xl mx-auto mb-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="relative z-50"
          >
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsFocused(true)}
                placeholder="Search news..."
                className="w-full px-4 py-3 pl-12 text-gray-700 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#D3242A] focus:ring-2 focus:ring-[#D3242A]/20 transition-all duration-200"
              />
              <svg 
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                />
              </svg>
            </div>
          </motion.div>

          <AnimatePresence>
            {isFocused && searchTerm && (
              <>
                {/* Full Page Overlay */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                  onClick={handleClose}
                />
                
                {/* Search Results Container */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.2 }}
                  className="fixed inset-x-0 top-[120px] bottom-0 bg-white z-50 overflow-y-auto"
                >
                  <div className="container mx-auto px-4 py-8">
                    <div className="flex justify-between items-center mb-8">
                      <h2 className="text-2xl font-bold text-gray-900">
                        Search Results for "{searchTerm}"
                      </h2>
                      <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <svg 
                          className="w-6 h-6 text-gray-500"
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M6 18L18 6M6 6l12 12" 
                          />
                        </svg>
                      </button>
                    </div>

                    {filteredNews.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredNews.map((news) => (
                          <div 
                            key={news.id} 
                            className="w-full max-w-[400px] cursor-pointer"
                            onClick={() => handleArticleClick(news)}
                          >
                            <BlogCard 
                              post={{
                                ...news,
                                link: `/${currentSection}/news/${news.slug}`
                              }} 
                              type="news" 
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-gray-500 text-lg">
                          No results found for "{searchTerm}"
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* Page Indicator */}
        <div className="text-center mb-8">
          <p className="text-gray-600">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredNews.length)} of {filteredNews.length} articles
          </p>
        </div>

        {/* Top Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-6 mb-8">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                currentPage > 1
                  ? 'bg-[#D3242A] text-white hover:bg-red-700'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              } transition-colors`}
              aria-label="Previous page"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-6 w-6" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M15 19l-7-7 7-7" 
                />
              </svg>
            </button>

            <div className="flex items-center space-x-4">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={`page-${page}`}
                  onClick={() => handlePageChange(page)}
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-medium transition-all transform hover:scale-110 ${
                    currentPage === page
                      ? 'bg-[#D3242A] text-white scale-110'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                currentPage < totalPages
                  ? 'bg-[#D3242A] text-white hover:bg-red-700'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              } transition-colors`}
              aria-label="Next page"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-6 w-6" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5l7 7-7 7" 
                />
              </svg>
            </button>
          </div>
        )}

        {/* News Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 max-w-[1920px] mx-auto"
          style={{
            gridTemplateColumns: `repeat(auto-fit, minmax(300px, 1fr))`,
            justifyItems: 'center'
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {currentNews.map((news) => (
            <div 
              key={news.id} 
              className="w-full max-w-[400px] cursor-pointer"
              onClick={() => handleArticleClick(news)}
            >
              <BlogCard 
                post={{
                  ...news,
                  link: `/${currentSection}/news/${news.slug}`
                }} 
                type="news" 
              />
            </div>
          ))}
        </motion.div>

        {filteredNews.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No news articles found matching your search.</p>
          </div>
        )}

        {/* Bottom Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-6 mt-8">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                currentPage > 1
                  ? 'bg-[#D3242A] text-white hover:bg-red-700'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              } transition-colors`}
              aria-label="Previous page"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-6 w-6" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M15 19l-7-7 7-7" 
                />
              </svg>
            </button>

            <div className="flex items-center space-x-4">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-medium transition-all transform hover:scale-110 ${
                    currentPage === page
                      ? 'bg-[#D3242A] text-white scale-110'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                currentPage < totalPages
                  ? 'bg-[#D3242A] text-white hover:bg-red-700'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              } transition-colors`}
              aria-label="Next page"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-6 w-6" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5l7 7-7 7" 
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InTheNews; 